import type React from 'react';
import {
  faChevronDown,
  faChevronUp,
  faMinus,
  faPlus,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import * as AccordionPrimitive from '@radix-ui/react-accordion';

import { Icon } from '@/components/ui/icon';
import { cn } from '@/lib/utils';

function Accordion({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return <AccordionPrimitive.Root data-slot="accordion" {...props} />;
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn('border-b last:border-b-0', className)}
      {...props}
    />
  );
}

function AccordionHeader({ ...props }: React.ComponentProps<typeof AccordionPrimitive.Header>) {
  return <AccordionPrimitive.Header {...props} />;
}

export type AccordionIconStyle = 'chevron' | 'plus';

function AccordionToggleIcons({ iconStyle }: { iconStyle: AccordionIconStyle }) {
  return (
    <>
      <span className="acc-icon-closed inline-flex items-center justify-center">
        <Icon icon={iconStyle === 'plus' ? faPlus : faChevronDown} className="size-3" />
      </span>
      <span className="acc-icon-open inline-flex items-center justify-center">
        <Icon icon={iconStyle === 'plus' ? faMinus : faChevronUp} className="size-3" />
      </span>
    </>
  );
}
export type AccordionButtonVariant = 'plain' | 'filled';

function AccordionChevronToggle({
  iconStyle,
  buttonVariant,
}: {
  iconStyle: AccordionIconStyle;
  buttonVariant: AccordionButtonVariant;
}) {
  return (
    <span
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-full transition-colors',
        buttonVariant === 'filled' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground',
      )}
    >
      <AccordionToggleIcons iconStyle={iconStyle} />
    </span>
  );
}

interface AccordionTriggerProps extends React.ComponentProps<typeof AccordionPrimitive.Trigger> {
  iconStyle?: AccordionIconStyle;
  buttonVariant?: AccordionButtonVariant;
}

function AccordionTrigger({
  className,
  children,
  iconStyle = 'chevron',
  buttonVariant = 'plain',
  ...props
}: AccordionTriggerProps) {
  const isPlainChevronTrigger = iconStyle === 'chevron' && buttonVariant === 'plain';
  const suffix = isPlainChevronTrigger ? (
    <Icon
      icon={faChevronDown}
      className="pointer-events-none size-4 shrink-0 translate-y-0.5 text-muted-foreground transition-transform duration-200"
    />
  ) : (
    <AccordionChevronToggle iconStyle={iconStyle} buttonVariant={buttonVariant} />
  );
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          'flex flex-1 items-start justify-between gap-4 rounded-md py-4 text-left text-sm font-medium transition-all outline-none hover:underline focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50',
          isPlainChevronTrigger
            ? '[&[data-state=open]>svg]:rotate-180'
            : '[&[data-state=open]_.acc-icon-closed]:hidden [&[data-state=closed]_.acc-icon-open]:hidden',
          className,
        )}
        {...props}
      >
        {children}
        {suffix}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  );
}

interface AccordionIconTriggerProps
  extends Omit<React.ComponentProps<typeof AccordionPrimitive.Trigger>, 'children'> {
  iconStyle?: AccordionIconStyle;
  buttonVariant?: AccordionButtonVariant;
}

function AccordionIconTrigger({
  iconStyle = 'chevron',
  buttonVariant = 'plain',
  className,
  ...props
}: AccordionIconTriggerProps) {
  return (
    <AccordionPrimitive.Trigger
      className={cn(
        'flex size-7 shrink-0 items-center justify-center rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring',
        '[&[data-state=open]_.acc-icon-closed]:hidden [&[data-state=closed]_.acc-icon-open]:hidden',
        buttonVariant === 'filled'
          ? 'bg-primary text-primary-foreground hover:bg-primary/80'
          : 'text-muted-foreground',
        className,
      )}
      {...props}
    >
      <AccordionToggleIcons iconStyle={iconStyle} />
    </AccordionPrimitive.Trigger>
  );
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className={cn(
        'overflow-hidden pt-0 pb-4 text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        className,
      )}
      {...props}
    >
      {children}
    </AccordionPrimitive.Content>
  );
}

export {
  Accordion,
  AccordionContent,
  AccordionHeader,
  AccordionIconTrigger,
  AccordionItem,
  AccordionTrigger,
};
